import { useRef } from "react";
import swal from "sweetalert";
import Select from "react-select";
import { Form } from "react-bootstrap";
import { selectCustomStyles } from "../../../../assets/global/selectCustomStyles";
import useDarkMode from "../../../../components/Themes/Usedarkmode";
import { formatToTwoDecimal } from "../../../../utils/numberFormatter";

const ProductionMaterialsCreateTable = ({
  id,
  edit,
  raws,
  vendor,
  onMenuOpen,
  menuIsOpen,
  selectedWarehouse,
  stock_raw_products,
  filteredRawProductOptions,
  setMaterialToDelete,
  materialToDelete,
  setRaws,
  validated,
  onInputFloat,
  finishedProductRows,
  deleteItemRaw,
  addNewItemRaw,
}) => {
  const warnedStocksRef = useRef(new Set());
  const { themeSelectStyles } = useDarkMode();

  return (
    <div className="mb-2 production-form-container-item">
      <div className="table-responsive">
        <table className="w-100">
          <thead className="bg-light-custom thead-custom">
            <tr>
              <th className="p-2">Supplier Code</th>
              <th className="p-2">Materials</th>
              <th className="p-2">UOM</th>
              <th className={`${id ? "d-none" : ""} p-2`}>Current Stock</th>
              <th className="p-2">Qty</th>
              <th className="p-2">Unit Price</th>
              <th className="p-2">Costing</th>
              <th className=""></th>
            </tr>
          </thead>
          <tbody>
            {raws.map((data, index) => {
              const stockRawProducts = filteredRawProductOptions(
                index,
                data.vendor_id,
              );

              const rawProductOptions = stockRawProducts.map((item) => ({
                value: item.product_id,
                label: item.product_list?.product_name || "Unnamed Product",
              }));

              const vendorValue = vendor.vendorOptions.find(
                (item) => item.value === data.vendor_id,
              );

              return (
                <tr className="custom-tr" key={`${data.stock_id}-${index}`}>
                  <td>
                    <Select
                      options={vendor.vendorOptions}
                      value={vendorValue}
                      onChange={(selectedOption) =>
                        vendor.handleSupplierCodeChange(
                          selectedOption,
                          index,
                          data,
                        )
                      }
                      placeholder={`Select Supplier Code`}
                      styles={themeSelectStyles(
                        selectCustomStyles(data.vendor_id, validated),
                      )}
                      onMenuOpen={onMenuOpen}
                      menuIsOpen={menuIsOpen}
                      menuPortalTarget={document.body}
                      isDisabled={edit !== undefined ? !edit : false}
                      isSearchable
                    />
                  </td>
                  <td className="custom-td">
                    <Select
                      options={rawProductOptions}
                      value={rawProductOptions.find(
                        (item) => String(item?.value) === String(data.stock_id),
                      )}
                      onChange={async (selectedOption) => {
                        const selected = selectedOption?.value;

                        const isExist = raws.some(
                          (mother) =>
                            String(mother.stock_id) === String(selected),
                        );

                        if (isExist) {
                          swal({
                            title: "Oppss!",
                            text: "You cannot add product that already exists",
                            icon: "error",
                            buttons: false,
                            timer: 2000,
                          });
                        } else {
                          const selectedProduct = stockRawProducts.find(
                            (product) =>
                              String(product.product_id) === String(selected),
                          );

                          // Update logic: track deleted materials
                          if (
                            id &&
                            !data.newItem &&
                            typeof setMaterialToDelete === "function"
                          ) {
                            setMaterialToDelete((prev) => [...prev, data]);
                          }

                          if (selectedProduct) {
                            setRaws((prevRaws) =>
                              prevRaws.map((item, i) =>
                                i === index
                                  ? {
                                      ...item,
                                      stock_id: selectedProduct.product_id,
                                      prod_id: selectedProduct.product_id,
                                      prod_code:
                                        selectedProduct.product_list
                                          ?.product_code || "",
                                      prod_name:
                                        selectedProduct.product_list
                                          ?.product_name || "",
                                      prod_uom:
                                        selectedProduct.product_list
                                          ?.unit_of_measure || "",
                                      current_stock:
                                        selectedProduct.totalStock || 0,
                                      prod_price:
                                        selectedProduct.averagePrice || 0,
                                      weightIn: "0",
                                      cost: "",
                                      newItem: true,
                                    }
                                  : item,
                              ),
                            );
                          }
                        }
                      }}
                      onMenuOpen={onMenuOpen}
                      menuIsOpen={menuIsOpen}
                      menuPortalTarget={document.body}
                      placeholder={`Select Materials`}
                      styles={themeSelectStyles(
                        selectCustomStyles(data.stock_id || true, validated),
                      )}
                      isDisabled={
                        edit !== undefined
                          ? !edit || !data.vendor_id
                          : !data.vendor_id
                      }
                      isSearchable
                    />
                  </td>

                  {/* UOM */}
                  <td className="custom-td">
                    <input
                      type="text"
                      value={data.prod_uom}
                      readOnly
                      className="form-control form-control-sm custom-input p-2"
                    />
                  </td>

                  {/* Current Stock */}
                  <td className={`${id ? "d-none" : "custom-td"}`}>
                    <input
                      type="text"
                      value={formatToTwoDecimal(data.current_stock)}
                      readOnly
                      className="form-control form-control-sm custom-input p-2"
                    />
                  </td>

                  {/* Weight In */}
                  <td className="custom-td">
                    <Form.Control
                      type="text"
                      value={data.weightIn}
                      disabled={edit !== undefined ? !edit : false}
                      onInput={onInputFloat}
                      onChange={(e) => {
                        const newWeight =
                          parseFloat(
                            String(e.target.value).replace(/,/g, ""),
                          ) || 0;
                        let inputValue = String(e.target.value).replace(
                          /[^0-9.]/g,
                          "",
                        );
                        let [integerPart, decimalPart] = inputValue.split(".");

                        if (
                          decimalPart !== undefined &&
                          decimalPart.length > 6
                        ) {
                          swal({
                            title: "Invalid Input",
                            text: "Maximum of 6 decimal places only.",
                            icon: "warning",
                            buttons: false,
                            timer: 2000,
                          });
                          decimalPart = decimalPart.slice(0, 6);
                        }

                        if (integerPart) {
                          integerPart = integerPart.replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ",",
                          );
                        }

                        let formattedValue =
                          decimalPart !== undefined
                            ? `${integerPart}.${decimalPart}`
                            : integerPart;

                        const cleanedValue = formattedValue.replace(
                          /^0+(?=\d)/,
                          "",
                        );

                        const currentStockVal =
                          parseFloat(data.current_stock) || 0;

                        if (
                          !id &&
                          newWeight > currentStockVal &&
                          data.stock_id &&
                          !warnedStocksRef.current.has(data.stock_id)
                        ) {
                          warnedStocksRef.current.add(data.stock_id);
                          swal({
                            title: "Stock Warning",
                            text: `The entered quantity is over the current available stock (${formatToTwoDecimal(currentStockVal)}).`,
                            icon: "warning",
                            button: "Ok, Proceed.",
                          });
                        }

                        setRaws((prevRaws) =>
                          prevRaws.map((item) =>
                            item.stock_id === data.stock_id
                              ? {
                                  ...item,
                                  weightIn: cleanedValue,
                                  cost:
                                    newWeight *
                                    parseFloat(data.prod_price || 0),
                                }
                              : item,
                          ),
                        );
                      }}
                      className="form-control form-control-sm custom-input p-2"
                    />
                  </td>

                  {/* Unit Price */}
                  <td className="custom-td">
                    <input
                      type="text"
                      value={formatToTwoDecimal(data.prod_price)}
                      className="form-control form-control-sm p-2"
                      readOnly
                    />
                  </td>

                  {/* Costing */}
                  <td className="custom-td">
                    <input
                      type="text"
                      value={formatToTwoDecimal(data.cost)}
                      readOnly
                      className="form-control form-control-sm p-2"
                    />
                  </td>

                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteItemRaw(index, data)}
                      disabled={
                        edit !== undefined
                          ? raws.length === 1 || !edit
                          : raws.length === 1
                      }
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        className={`w-100 ${edit !== undefined && !edit ? "d-none" : ""} d-flex justify-content-end mt-2 p-1`}
      >
        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={addNewItemRaw}
        >
          New Item
        </button>
      </div>
      {/* <div className="d-flex gap-4 mt-2 text-white flex-row justify-content-evenly px-3 total-amount-container-production align-items-center rounded">
            <div>
              <p className="mb-0">
                Total Weight In:{" "}
                <span>
                  {materialsTotalWeight.toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  })}
                </span>{" "}
              </p>
            </div>
            <div>
              <p className="mb-0">
                Total Costing:{" "}
                <span>
                  {materialsTotalCosting.toLocaleString("en-US", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  })}
                </span>
              </p>
            </div>
          </div> */}
    </div>
  );
};

export default ProductionMaterialsCreateTable;
